import type { FileRecord, FileRecordDisplayKind } from "@cohesive/api-client";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  BLOCK_ACTION_ACTIVATION_MS,
  BLOCK_FILE_ACTION_ACTIVATION_MS,
  toBlockActionError,
  type BlockActionHandler,
} from "../../../lib/blockActions";
import type { BlockDisplay } from "../../../lib/blockDisplay";
import { loadBlockFonts } from "../../../lib/blockFonts";
import { followBlockLink } from "../../../lib/blockLinkNavigation";
import {
  blockWantsStateSource,
  type BlockStateSource,
} from "../../../lib/blockStateSource";
import {
  createBlockActionResultMessage,
  createBlockClickMessage,
  createBlockConfigMessage,
  createBlockFontsMessage,
  createBlockHoverLeaveMessage,
  createBlockHoverMessage,
  createBlockIslandsMessage,
  createBlockLinksMessage,
  createBlockViewRestoreMessage,
  parseBlockMessage,
  type BlockActionError,
  type BlockEntryPoint,
  type BlockHoverKind,
  type BlockToParentMessage,
  type BlockViewState,
} from "../../../lib/htmlBlockRuntime";
import {
  blockPrepareOptionsSalt,
  buildBlockPrepareOptions,
  prepareSrcDocHtml,
} from "../../../lib/htmlIframeSrcDoc";
import { cn } from "../../../lib/utils";
import { getFileNameFromPath } from "../FileRecordCard/FileRecordCard";
import { HtmlBlockShield } from "./HtmlBlockShield";
import { HtmlCardPlaceholder } from "./HtmlCardPlaceholder";
import { useBlockStateBridge } from "./useBlockStateBridge";
import { useHtmlCardSrcDoc } from "./useHtmlCardSrcDoc";
import {
  useIframeMountAdmission,
  type IframeMountPolicy,
} from "./useIframeMountAdmission";

/** A new document is shown anyway after this, even if it never reports its
 *  restore (a runtime older than the message). */
const VIEW_RESTORE_TIMEOUT_MS = 4_000;
/**
 * How long the poster stays up waiting for the runtime's `settled` before the
 * document is shown anyway. Same cap as the lightbox. It covers a document
 * with no runtime to send the message, and a runtime too old to have it.
 */
const SETTLE_CAP_MS = 2_000;

export type HtmlBlockCardCanvasProps = Pick<
  FileRecord,
  "url" | "path" | "updated_at"
> & {
  /** Names the bytes; `updated_at` stands in for a row without one. */
  content_hash?: string;
  className?: string;
  style?: React.CSSProperties;
  selected?: boolean;
  /** The user is INSIDE the block (interact or edit mode) — the chrome reads
   *  blue to distinguish focus-for-interaction from a mere selection. */
  focused?: boolean;
  /** True while the parent NodeResizer is active — the shield remounts so
   *  the resize cursor isn't captured by the iframe. */
  resizing?: boolean;
  /** True when the iframe should receive pointer events (block entered by
   *  double-click, minus gesture exceptions — EntityNode owns that
   *  expression). */
  isInteractive?: boolean;
  /** The block's display model (chromeless etc.), resolved by the caller
   *  through blockDisplayFromNodeData. */
  display: BlockDisplay;
  /** `"progressive"` paces the iframe mount through the shared admission
   *  scheduler (public share view); default `"eager"` mounts as soon as the
   *  document is ready — the editor's unchanged behavior. */
  mountPolicy?: IframeMountPolicy;
  /** Server-rendered card preview shown until the live iframe is up —
   *  the share view's placeholder while a progressive mount waits its turn.
   *  Falls back to the loading skeleton when absent. */
  previewImageUrl?: string | null;
  /** Active app theme; drives the injected color-scheme so transparent blocks
   *  composite correctly (mismatched schemes paint an opaque backstop).
   *  Defaults to light. */
  theme?: "light" | "dark";
  /** Optional async rewrite applied to the fetched HTML before rendering —
   *  used to swap embedded public file URLs for presigned ones. Memoize it;
   *  identity changes do not retrigger a fetch. */
  transformHtml?: (html: string) => Promise<string> | string;
  /** Edit session active: the block runtime enables contenteditable islands
   *  and the iframe receives pointer events. */
  islandsEditable?: boolean;
  /** While true, refetched content is stashed instead of committed so the
   *  iframe never remounts mid edit-session. */
  contentFrozen?: boolean;
  /** Capability-aware affordance. The label is painted by node chrome
   *  outside the block box; here it only marks the shield as a click target.
   *  Null/undefined means the block is live or loading. */
  hintLabel?: string | null;
  /** Hovering the card should faintly outline editable islands (host-driven
   *  through the runtime — the shield keeps :hover out of the iframe). */
  islandHint?: boolean;
  /** Caret target for an edit session entered by double-click, in iframe
   *  content coordinates. Forwarded with the runtime config. */
  editFocusPoint?: BlockEntryPoint | null;
  /** The runtime announced itself for the current document; `islands` is the
   *  live count of editable islands inside the opaque frame. */
  onRuntimeReady?: (info: { islands: number }) => void;
  /** Runtime messages (island edits, flush/exit requests, edit requests) —
   *  link and click-result messages are handled internally. */
  onBlockMessage?: (msg: BlockToParentMessage) => void;
  /** A link in the block resolved to this origin: `to` is its path, query
   *  and hash, for the host's router. A fragment-only viewport deep link
   *  (`#x=&y=&z=&t=`) arrives as the current path with that hash. Return
   *  `false` to decline it; a declined link, or any same-origin link when
   *  this is absent, opens in a new tab like any other https link, and a
   *  fragment-only one is dropped. */
  onNavigate?: (to: string) => boolean | void;
  /** Runs the block's `cohesive.navigate / chat / generate` calls as the
   *  viewer. Absent: every call is answered `unavailable`. A call reaches
   *  it only from inside a click — the block is live, or a click was
   *  forwarded moments ago. */
  onAction?: BlockActionHandler;
  onDoubleClick?: (point?: BlockEntryPoint) => void;
  /**
   * Forward qualifying single clicks into a resting block as activations
   * while the shield keeps the real pointer. The caller owns the canvas-side
   * guards (multi-select, hand tool, dragging); the card only enforces the
   * gesture-shape ones — first click, no selection modifiers, no movement
   * since pointerdown — because only it sees the raw event.
   */
  clickThrough?: boolean;
  /**
   * A forwarded click landed on a control. The doorway of the interaction
   * model: the caller responds by entering inline interactive mode, so the
   * first activation is forwarded and everything after is native.
   */
  onInteractiveClickThrough?: () => void;
  /**
   * The current document has been revealed — fired from the same keyed
   * reveal that swaps the placeholder out, so it inherits the load-or-ready
   * race protection (once per document, whichever signal lands first, never
   * for a replaced document). The lightbox animates its stage in on this
   * rather than around an empty card.
   */
  onDisplayReady?: () => void;
  /** Backs the block's `cohesive.state`. Absent: an empty read-only state
   *  (or the snapshot a published document carries). */
  blockState?: BlockStateSource;
  /** The record's `display.kind`: every block but a static one gets
   *  `blockState`. */
  kind?: FileRecordDisplayKind | null;
};

/**
 * Canvas HTML block: the document rendered 1:1 at exactly the node's size
 * with the app color scheme, Cohesive tokens, fonts, and the cohesive-block
 * runtime injected. One srcDoc serves display, interactive, and edit modes —
 * the parent flips modes via config messages, never remounts, so in-block JS
 * state survives them (see useHtmlCardSrcDoc for the remount discipline).
 *
 * Pointer ownership: unless the block is live (inline interactive or an edit
 * session), an HtmlBlockShield sits above the iframe so every canvas gesture
 * — wheel pan, drag pan, node drag, marquee — works exactly as over any other
 * node. Single clicks at rest are forwarded through the shield into the
 * frame (click-through); a click that lands on a control is the doorway
 * into inline interactive mode.
 */
export function HtmlBlockCardCanvas({
  url,
  path,
  content_hash,
  updated_at,
  className,
  style,
  selected,
  focused = false,
  resizing = false,
  isInteractive = false,
  display,
  mountPolicy = "eager",
  previewImageUrl = null,
  theme = "light",
  transformHtml,
  islandsEditable = false,
  contentFrozen = false,
  hintLabel = null,
  islandHint = false,
  editFocusPoint = null,
  onRuntimeReady,
  onBlockMessage,
  onNavigate,
  onAction,
  onDoubleClick,
  clickThrough = false,
  onInteractiveClickThrough,
  onDisplayReady,
  blockState,
  kind,
}: HtmlBlockCardCanvasProps) {
  const iframeActive = (isInteractive || islandsEditable) && !resizing;

  // Chromeless = no card chrome AND a transparent ground: the HTML
  // composites straight onto the canvas.
  const frameless = display.chromeless;
  const transparent = display.chromeless;

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const hostRef = useRef<HTMLDivElement>(null);
  // The document currently revealed, by its mount key. Deriving visibility
  // from the key (rather than a boolean reset in an effect) means a reveal
  // can never be undone by a later effect and a signal from a replaced
  // document can never reveal the new one — whatever order `load`, the
  // runtime's `ready` and React's effects arrive in. WebKit fires `load`
  // for a srcdoc frame within a few ms of insertion, before passive
  // effects have run; the boolean version lost that reveal.
  const [revealedKey, setRevealedKey] = useState<string | null>(null);
  const [hovered, setHovered] = useState(false);

  // The theme is read through a ref: the document is prepared for the theme
  // of the moment it mounts, and later flips reach the running runtime as
  // config (applied in place) — so neither `prepare`'s identity nor the
  // options salt may change with it, or every block would refetch and
  // remount on a theme toggle.
  const themeRef = useRef(theme);
  themeRef.current = theme;
  // The viewer is baked into the document (and the salt) so a block's
  // scripts can read `cohesive.viewer` at once; it changes only when the
  // signed-in user does.
  const sourceBoot = blockState?.getBoot();
  const bootViewer = sourceBoot?.viewer ?? null;
  const bootReadonly = sourceBoot?.readonly ?? true;
  const boot = useMemo(
    () => ({ viewer: bootViewer, readonly: bootReadonly }),
    // Keyed on fields: every card rebuilds an equal viewer object.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bootViewer?.id, bootViewer?.name, bootViewer?.avatarUrl, bootReadonly],
  );
  const prepare = useCallback(
    (transformed: string) =>
      prepareSrcDocHtml(
        transformed,
        buildBlockPrepareOptions({
          theme: themeRef.current,
          transparent,
          boot,
        }),
      ),
    [transparent, boot],
  );

  const postToRuntime = useCallback((message: unknown) => {
    iframeRef.current?.contentWindow?.postMessage(message, "*");
  }, []);

  const onLinksRenewed = useCallback(
    (map: Record<string, string>) =>
      postToRuntime(createBlockLinksMessage(map)),
    [postToRuntime],
  );
  const onIslandsChanged = useCallback(
    (regions: Record<string, string>) =>
      postToRuntime(createBlockIslandsMessage(regions)),
    [postToRuntime],
  );

  // Public file links the block started using (or was handed by a file
  // action), each with the signed link it currently maps to. Resolved
  // through the same transform as the document's own links, so they share
  // one cache and renew together.
  const transformHtmlRef = useRef(transformHtml);
  transformHtmlRef.current = transformHtml;
  const fileLinksRef = useRef<Map<string, string>>(new Map());
  const resolveFileLinks = useCallback(
    async (urls: string[], renew: boolean) => {
      const transform = transformHtmlRef.current;
      if (!transform || urls.length === 0) return;
      let out: string;
      try {
        out = await transform(urls.join("\n"));
      } catch {
        return;
      }
      const got = out.split("\n");
      const map: Record<string, string> = {};
      urls.forEach((publicUrl, i) => {
        const next = got[i];
        if (!next || next === publicUrl) return;
        const prev = fileLinksRef.current.get(publicUrl);
        if (!renew) map[publicUrl] = next;
        if (prev && prev !== next) map[prev] = next;
        fileLinksRef.current.set(publicUrl, next);
      });
      if (Object.keys(map).length > 0) {
        postToRuntime(createBlockLinksMessage(map));
      }
    },
    [postToRuntime],
  );
  // The transform changes identity when a link renews: re-resolve what was
  // handed out so the frame's copies renew with the document's.
  useEffect(() => {
    void resolveFileLinks(Array.from(fileLinksRef.current.keys()), true);
  }, [transformHtml, resolveFileLinks]);
  const rememberFileLinks = useCallback((result: unknown) => {
    for (const item of Array.isArray(result) ? result : [result]) {
      if (!item || typeof item !== "object") continue;
      const { publicUrl, url } = item as { publicUrl?: unknown; url?: unknown };
      if (typeof publicUrl === "string" && typeof url === "string") {
        fileLinksRef.current.set(publicUrl, url);
      }
    }
  }, []);

  // A capture card past the page's document cap stays on its poster and
  // never needs its body, so it enters the scheduler before the read and
  // reads only once admitted (under "poster", never). Everywhere else a card
  // enters once its document is ready, so a slot always paces a real mount.
  const captureMount = mountPolicy === "capture" || mountPolicy === "poster";
  const [bodyWanted, setBodyWanted] = useState(!captureMount);

  const { displayDoc } = useHtmlCardSrcDoc({
    url,
    path,
    content_hash,
    updated_at,
    transformHtml,
    optionsSalt: blockPrepareOptionsSalt(transparent, boot),
    prepare,
    contentFrozen,
    onLinksRenewed,
    onIslandsChanged,
    enabled: bodyWanted,
  });

  const stateSource = blockWantsStateSource(kind) ? blockState : undefined;
  const stateBridge = useBlockStateBridge(iframeRef, stateSource, bootViewer);
  const { onRuntimeReady: onStateReady, onStateOps } = stateBridge;

  // `ready` keeps this card out of the scheduler until its document can
  // actually mount, so an admission slot always paces a real iframe.
  const { admitted, onIframeLoad } = useIframeMountAdmission({
    policy: mountPolicy,
    hostRef,
    ready: captureMount || displayDoc !== null,
    // Exactly the condition under which the placeholder below shows a picture.
    hasPoster: (!transparent || captureMount) && previewImageUrl != null,
  });

  useEffect(() => {
    if (admitted) setBodyWanted(true);
  }, [admitted]);

  // Two steps, each once per document.
  //
  // (1) The document is far enough along to stop holding the pipe: whichever
  // of the iframe's `load` event and the runtime's `ready` message lands
  // first. `load` waits for every subresource, so a stalled third-party fetch
  // (a fonts host under Safari's Private Relay or tracking prevention) must
  // not be what returns the admission slot; `ready` is DOMContentLoaded. The
  // second signal is a no-op, so the slot is returned exactly once.
  //
  // (2) The poster comes down. NOT at step (1): the runtime posts `ready`
  // before its first paint, so swapping there shows an empty frame for a
  // beat — the flash this split exists to remove. The runtime's `settled`
  // (load + web fonts + a laid-out frame) is the paint, with a cap armed at
  // step (1) for documents that never send it.
  const mountKey = displayDoc?.mountKey ?? null;
  const mountKeyRef = useRef(mountKey);
  useLayoutEffect(() => {
    mountKeyRef.current = mountKey;
  }, [mountKey]);
  const slotReturnedForRef = useRef<string | null>(null);
  const onDisplayReadyRef = useRef(onDisplayReady);
  onDisplayReadyRef.current = onDisplayReady;
  const settleCapRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const revealDocument = useCallback((key: string) => {
    if (settleCapRef.current !== null) {
      clearTimeout(settleCapRef.current);
      settleCapRef.current = null;
    }
    // The cap is armed per document; a later one must not inherit it.
    if (mountKeyRef.current !== key) return;
    setRevealedKey((prev) => (prev === key ? prev : key));
  }, []);
  const markDisplayReady = useCallback(() => {
    const key = mountKeyRef.current;
    // Once-per-document latch: a raw onLoad would fire for a replaced
    // document and again after `ready`.
    if (!key || slotReturnedForRef.current === key) return;
    slotReturnedForRef.current = key;
    onIframeLoad();
    onDisplayReadyRef.current?.();
    // The previous document's cap would fire on this one's key check and
    // clear the cap armed here on its way out.
    if (settleCapRef.current !== null) clearTimeout(settleCapRef.current);
    settleCapRef.current = setTimeout(() => revealDocument(key), SETTLE_CAP_MS);
  }, [onIframeLoad, revealDocument]);
  useEffect(
    () => () => {
      if (settleCapRef.current !== null) clearTimeout(settleCapRef.current);
    },
    [],
  );
  // --- view state across documents ------------------------------------------
  // The runtime reports where the reader is; the latest report is handed to
  // the next document of this block — a reload after an edit, which must not
  // land the reader back at the top.
  const viewStateRef = useRef<BlockViewState | null>(null);
  // The document the report came from; a document never restores its own.
  const viewStateDocKeyRef = useRef<string | null>(null);

  // A document that will restore a view stays hidden until it reports back,
  // so the reader never sees it at the top first. Read during render, not
  // set from an effect: WebKit fires `load` on a srcdoc frame before passive
  // effects run, and the reveal would then land first. The placeholder
  // covers the gap, and the cap keeps a runtime that never answers from
  // hiding the document for good.
  const [restoredKey, setRestoredKey] = useState<string | null>(null);
  // Latched once per document, during its first render: the document's own
  // `view-state` (a scroll report that can arrive before the restore has
  // been applied) moves `viewStateDocKeyRef`, and re-deriving from it would
  // lift the hide at the next render of the canvas.
  const willRestoreRef = useRef<{ key: string | null; will: boolean }>({
    key: null,
    will: false,
  });
  if (willRestoreRef.current.key !== mountKey) {
    willRestoreRef.current = {
      key: mountKey,
      will:
        mountKey !== null &&
        viewStateRef.current !== null &&
        viewStateDocKeyRef.current !== mountKey,
    };
  }
  const restoring = willRestoreRef.current.will && restoredKey !== mountKey;
  const isVisible = mountKey !== null && revealedKey === mountKey && !restoring;
  useEffect(() => {
    if (!restoring) return;
    const timer = setTimeout(
      () => setRestoredKey(mountKey),
      VIEW_RESTORE_TIMEOUT_MS,
    );
    return () => clearTimeout(timer);
  }, [restoring, mountKey]);

  // --- cohesive-block bridge -----------------------------------------------

  const islandsEditableRef = useRef(islandsEditable);
  islandsEditableRef.current = islandsEditable;
  const isInteractiveRef = useRef(isInteractive);
  isInteractiveRef.current = isInteractive;
  const onBlockMessageRef = useRef(onBlockMessage);
  onBlockMessageRef.current = onBlockMessage;
  const onNavigateRef = useRef(onNavigate);
  onNavigateRef.current = onNavigate;
  const onActionRef = useRef(onAction);
  onActionRef.current = onAction;
  // When the person last clicked the block: a click forwarded into the
  // resting frame, or a native one through the hole the shield opens over
  // a hovered control (the runtime answers both with a click-result). An
  // action from the block counts as the person's only inside that window.
  const lastGestureAtRef = useRef(0);
  const onRuntimeReadyRef = useRef(onRuntimeReady);
  onRuntimeReadyRef.current = onRuntimeReady;
  const hintRef = useRef(false);
  hintRef.current = hovered && islandHint;
  const editFocusPointRef = useRef(editFocusPoint);
  editFocusPointRef.current = editFocusPoint;

  const postRuntimeConfig = useCallback(() => {
    iframeRef.current?.contentWindow?.postMessage(
      createBlockConfigMessage({
        editable: islandsEditableRef.current,
        interactive: isInteractiveRef.current,
        hint: hintRef.current,
        focusPoint: editFocusPointRef.current,
        theme: themeRef.current,
      }),
      "*",
    );
  }, []);

  // The block webfonts, handed over the bridge (see blockFonts.ts): the
  // frame's opaque origin has no HTTP cache, so it must not fetch them
  // itself. Sent once per document — on the runtime's request at boot and,
  // as a backstop, on `ready` — to the frame that asked; a document replaced
  // in between never receives the previous one's fonts.
  const fontsSentForKeyRef = useRef<string | null>(null);
  const provisionFonts = useCallback(() => {
    const frame = iframeRef.current;
    const key = mountKeyRef.current;
    if (!frame || !key || fontsSentForKeyRef.current === key) return;
    fontsSentForKeyRef.current = key;
    void loadBlockFonts()
      .then((faces) => {
        if (iframeRef.current !== frame || mountKeyRef.current !== key) return;
        frame.contentWindow?.postMessage(createBlockFontsMessage(faces), "*");
      })
      .catch(() => {
        // The frame renders on the system stacks after its own short wait;
        // the next document may ask again.
        if (fontsSentForKeyRef.current === key) {
          fontsSentForKeyRef.current = null;
        }
      });
  }, []);

  // When a forwarded click last hit something interactive. Rapid clicks on a
  // control (a stepper, a checkbox) must read as repeated activation — the
  // second click is forwarded too, and the double-click gesture is
  // suppressed, so operating a control can never yank the user into a
  // different surface.
  const lastInteractiveClickAtRef = useRef(0);
  const INTERACTIVE_DOUBLE_CLICK_WINDOW_MS = 500;
  const onInteractiveClickThroughRef = useRef(onInteractiveClickThrough);
  onInteractiveClickThroughRef.current = onInteractiveClickThrough;
  // An island click answers with an edit-request AND a click-result for the
  // same gesture, back to back. The edit-request opens an edit session; the
  // click-result must NOT then take the interactive doorway — the host's
  // mode state hasn't re-rendered yet, so a guard on it cannot see the
  // session it would be downgrading. Suppress the doorway for exactly the
  // result that follows an edit-request.
  const editRequestPendingRef = useRef(false);
  // Forwarded clicks answered so far vs. sent: a double-click decision must
  // wait for the pair's results (the answer to "did either click hit a
  // control" lives in the frame), so a gesture that fires before the last
  // result returns is queued and decided when it lands.
  const pendingForwardsRef = useRef(0);
  const queuedDoubleClickRef = useRef<BlockEntryPoint | null>(null);

  useEffect(() => {
    const listener = (event: MessageEvent) => {
      const frame = iframeRef.current;
      // Authenticate by source, NOT origin: every opaque-origin frame on the
      // page reports its origin as the string "null", so an origin check
      // would accept messages from any other block.
      if (!frame || event.source !== frame.contentWindow) return;
      const msg = parseBlockMessage(event.data);
      if (!msg) return;
      if (msg.type === "fonts-request") {
        provisionFonts();
        return;
      }
      if (msg.type === "ready") {
        provisionFonts();
        markDisplayReady();
        postRuntimeConfig();
        onStateReady();
        const restore = viewStateRef.current;
        if (restore && viewStateDocKeyRef.current !== mountKeyRef.current) {
          frame.contentWindow?.postMessage(
            createBlockViewRestoreMessage(restore),
            "*",
          );
        }
        onRuntimeReadyRef.current?.({ islands: msg.islands ?? 0 });
        return;
      }
      if (msg.type === "settled") {
        markDisplayReady();
        const key = mountKeyRef.current;
        if (key) revealDocument(key);
        // The lightbox and the detail pane wait on this one too — they
        // reveal and size on it, and a swallowed `settled` leaves both
        // sitting on their own 2 s caps.
        onBlockMessageRef.current?.(msg);
        return;
      }
      if (msg.type === "state-ops") {
        onStateOps(msg.reqId, msg.ops);
        return;
      }
      if (msg.type === "view-state") {
        viewStateRef.current = msg.state;
        viewStateDocKeyRef.current = mountKeyRef.current;
        return;
      }
      if (msg.type === "view-restored") {
        setRestoredKey(mountKeyRef.current);
        return;
      }
      if (msg.type === "hover-result") {
        // Only answers from the current hover generation count: the host is
        // the authority on whether the pointer is over the block, and an
        // answer that crossed a leave in flight must not reopen the shield.
        if (msg.gen === hoverGenRef.current) setHoverKind(msg.kind);
        return;
      }
      if (msg.type === "context-menu") {
        // Re-raise as a real contextmenu event on the card, at the screen
        // position the content point maps to, so it bubbles into the canvas
        // node's handler exactly like a right-click on the shield.
        const host = hostRef.current;
        if (!host) return;
        const rect = host.getBoundingClientRect();
        const scale = host.offsetWidth > 0 ? rect.width / host.offsetWidth : 1;
        host.dispatchEvent(
          new MouseEvent("contextmenu", {
            bubbles: true,
            cancelable: true,
            button: 2,
            clientX: rect.left + msg.x * scale,
            clientY: rect.top + msg.y * scale,
          }),
        );
        return;
      }
      if (msg.type === "edit-request") {
        editRequestPendingRef.current = true;
        onBlockMessageRef.current?.(msg);
        return;
      }
      if (msg.type === "click-result") {
        lastGestureAtRef.current = Date.now();
        const afterEditRequest = editRequestPendingRef.current;
        editRequestPendingRef.current = false;
        pendingForwardsRef.current = Math.max(
          0,
          pendingForwardsRef.current - 1,
        );
        if (msg.interactive) {
          lastInteractiveClickAtRef.current = Date.now();
          // The doorway: a control took the click, so hand the block the
          // real pointer — everything after this is native events. Not
          // for editable text: that gesture already opened an edit session.
          if (!afterEditRequest) onInteractiveClickThroughRef.current?.();
        } else {
          // A plain click closes the window: the double-click guard asks
          // whether EITHER constituent click of a pair hit a control, and
          // that is a property of the latest forwarded click, not of any
          // control clicked earlier within the same half-second.
          lastInteractiveClickAtRef.current = 0;
        }
        // A text-entry control took the caret in-frame; focus the iframe
        // element so keystrokes route into that document — otherwise typing
        // falls through to canvas hotkeys (Delete removes the node…).
        // Retried after a beat: entering inline interactive re-renders the
        // node and React Flow's selection focus steals the frame's focus
        // back in the same tick.
        if (msg.focus) {
          frame.focus();
          window.setTimeout(() => iframeRef.current?.focus(), 60);
        }
        // A double-click that arrived while this result was in flight can
        // now be decided with the pair fully answered.
        if (pendingForwardsRef.current === 0 && queuedDoubleClickRef.current) {
          const point = queuedDoubleClickRef.current;
          queuedDoubleClickRef.current = null;
          decideDoubleClickRef.current(point);
        }
        return;
      }
      if (msg.type === "action") {
        const { id, name, input } = msg;
        const answer = (result?: unknown, error?: BlockActionError) =>
          frame.contentWindow?.postMessage(
            createBlockActionResultMessage(
              error ? { id, ok: false, error } : { id, ok: true, result },
            ),
            "*",
          );
        const window_ =
          name === "files.upload"
            ? BLOCK_FILE_ACTION_ACTIVATION_MS
            : BLOCK_ACTION_ACTIVATION_MS;
        const activated =
          isInteractiveRef.current ||
          Date.now() - lastGestureAtRef.current < window_;
        if (!activated) {
          answer(undefined, {
            code: "activation",
            message: `cohesive.${name} runs only inside a click.`,
          });
          return;
        }
        const handler = onActionRef.current;
        if (!handler) {
          answer(undefined, {
            code: "unavailable",
            message: "This surface cannot act on the canvas.",
          });
          return;
        }
        void Promise.resolve()
          .then(() => handler(name, input))
          .then(
            (result) => {
              rememberFileLinks(result);
              answer(result);
            },
            (err: unknown) => answer(undefined, toBlockActionError(err)),
          );
        return;
      }
      if (msg.type === "files-resolve") {
        void resolveFileLinks(msg.urls, false);
        return;
      }
      if (msg.type === "link") {
        // Raw href from the runtime; followed synchronously, inside the
        // click's user activation.
        followBlockLink(msg.href, onNavigateRef.current);
        return;
      }
      onBlockMessageRef.current?.(msg);
    };
    window.addEventListener("message", listener);
    return () => window.removeEventListener("message", listener);
  }, [
    markDisplayReady,
    postRuntimeConfig,
    provisionFonts,
    revealDocument,
    onStateReady,
    onStateOps,
  ]);

  // Mode flips (and hover-hint / caret-target / theme changes) re-send config
  // to the already-mounted runtime (fresh mounts are covered by the ready
  // handshake). A theme change is applied in place there — never a remount.
  useEffect(() => {
    postRuntimeConfig();
  }, [
    islandsEditable,
    isInteractive,
    hovered,
    islandHint,
    editFocusPoint,
    theme,
    postRuntimeConfig,
  ]);

  useEffect(() => {
    // A replaced document answers none of the previous one's clicks.
    pendingForwardsRef.current = 0;
    queuedDoubleClickRef.current = null;
  }, [mountKey]);

  // Resting hover, forwarded like clicks: the shield holds the pointer, so
  // the frame never sees :hover. One classification per animation frame
  // while the pointer moves over a resting block; the answer drives the
  // shield's cursor so the gesture is announced before it happens.
  const [hoverKind, setHoverKind] = useState<BlockHoverKind>("none");
  const hoverRafRef = useRef(0);
  const hoverPointRef = useRef<BlockEntryPoint | null>(null);
  // Bumped on every leave (and on going live); answers carrying an older
  // generation are dropped.
  const hoverGenRef = useRef(0);
  const handleMouseMove = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!clickThrough || iframeActive) return;
      const el = event.currentTarget;
      const rect = el.getBoundingClientRect();
      const scale = el.offsetWidth > 0 ? rect.width / el.offsetWidth : 1;
      hoverPointRef.current = {
        x: (event.clientX - rect.left) / scale,
        y: (event.clientY - rect.top) / scale,
      };
      if (hoverRafRef.current) return;
      hoverRafRef.current = window.requestAnimationFrame(() => {
        hoverRafRef.current = 0;
        const point = hoverPointRef.current;
        if (point) {
          postToRuntime(createBlockHoverMessage(point, hoverGenRef.current));
        }
      });
    },
    [clickThrough, iframeActive, postToRuntime],
  );
  const handleMouseLeave = useCallback(() => {
    setHovered(false);
    if (hoverRafRef.current) {
      window.cancelAnimationFrame(hoverRafRef.current);
      hoverRafRef.current = 0;
    }
    hoverPointRef.current = null;
    hoverGenRef.current += 1;
    setHoverKind("none");
    postToRuntime(createBlockHoverLeaveMessage());
  }, [postToRuntime]);
  // Over a control the shield steps aside: the control is native (its own
  // hover state, drags, wheel), and the runtime reports both the click (the
  // doorway) and the pointer leaving it (which closes the hole again).
  // Islands stay behind the shield — their click is an edit request.
  const shieldOpen =
    !iframeActive &&
    clickThrough &&
    (hoverKind === "control" ||
      hoverKind === "pointer" ||
      hoverKind === "link" ||
      hoverKind === "field");
  // A live frame owns its own hover; drop the resting tag when it goes live.
  useEffect(() => {
    if (iframeActive) {
      hoverGenRef.current += 1;
      setHoverKind("none");
      postToRuntime(createBlockHoverLeaveMessage());
    }
  }, [iframeActive, postToRuntime]);

  // Where the pointer went down, to tell a click from a completed drag —
  // React Flow can let a click fire after a node drag, and forwarding that
  // would push a button the user was only moving.
  const clickPointerDownRef = useRef<{ x: number; y: number } | null>(null);
  const handlePointerDownCapture = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      clickPointerDownRef.current = { x: event.clientX, y: event.clientY };
    },
    [],
  );

  const handleRootClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!clickThrough || iframeActive) return;
      // First clicks always forward. A second click (detail 2) forwards ONLY
      // when the first landed on a control — a stepper clicked fast gets both
      // activations — and never when it landed on plain content, where the
      // pair is a double-click gesture instead.
      if (event.detail !== 1) {
        const sinceInteractive = Date.now() - lastInteractiveClickAtRef.current;
        if (
          event.detail !== 2 ||
          sinceInteractive > INTERACTIVE_DOUBLE_CLICK_WINDOW_MS
        ) {
          return;
        }
      }
      // Selection modifiers are canvas gestures (shift-click adds to the
      // selection); never steal them for the content.
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }
      const down = clickPointerDownRef.current;
      if (
        down &&
        (Math.abs(event.clientX - down.x) > 4 ||
          Math.abs(event.clientY - down.y) > 4)
      ) {
        return;
      }
      const el = event.currentTarget;
      const rect = el.getBoundingClientRect();
      const scale = el.offsetWidth > 0 ? rect.width / el.offsetWidth : 1;
      pendingForwardsRef.current += 1;
      lastGestureAtRef.current = Date.now();
      postToRuntime(
        createBlockClickMessage({
          x: (event.clientX - rect.left) / scale,
          y: (event.clientY - rect.top) / scale,
        }),
      );
    },
    [clickThrough, iframeActive, postToRuntime],
  );

  // The double-click decision proper, run once the pair's results are in.
  // Operating a control is never also a gesture: if either constituent click
  // hit something interactive, the pair stays two activations.
  const onDoubleClickRef = useRef(onDoubleClick);
  onDoubleClickRef.current = onDoubleClick;
  const clickThroughRef = useRef(clickThrough);
  clickThroughRef.current = clickThrough;
  const decideDoubleClickRef = useRef((point: BlockEntryPoint) => {
    if (
      clickThroughRef.current &&
      Date.now() - lastInteractiveClickAtRef.current <
        INTERACTIVE_DOUBLE_CLICK_WINDOW_MS
    ) {
      return;
    }
    onDoubleClickRef.current?.(point);
  });

  const handleDoubleClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!onDoubleClick) return;
      // Convert to iframe content coordinates: the card may be scaled by the
      // React Flow viewport, so divide the on-screen offset back down by the
      // rendered/layout width ratio.
      const el = event.currentTarget;
      const rect = el.getBoundingClientRect();
      const scale = el.offsetWidth > 0 ? rect.width / el.offsetWidth : 1;
      const point = {
        x: (event.clientX - rect.left) / scale,
        y: (event.clientY - rect.top) / scale,
      };
      // Results still in flight for the pair's clicks: decide when they
      // land rather than guessing from a stale timestamp. A runtime that
      // never answers (no script, frame replaced) must not strand the
      // gesture — a short fallback decides with what is known.
      if (clickThrough && pendingForwardsRef.current > 0) {
        queuedDoubleClickRef.current = point;
        window.setTimeout(() => {
          if (queuedDoubleClickRef.current !== point) return;
          queuedDoubleClickRef.current = null;
          pendingForwardsRef.current = 0;
          decideDoubleClickRef.current(point);
        }, 250);
        return;
      }
      decideDoubleClickRef.current(point);
    },
    [onDoubleClick, clickThrough],
  );

  const displayTitle = (path && getFileNameFromPath(path)) || "HTML";

  return (
    <div
      ref={hostRef}
      className={cn(
        "group relative h-full w-full overflow-hidden",
        !frameless && "rounded-xl",
        // At rest a selection ring; inside the block none — the node's
        // resizer frame turns blue and is the single live border.
        selected && !focused && "ring-2 ring-canvas-selection/60",
        className,
      )}
      style={{ colorScheme: theme, ...style }}
      onDoubleClick={handleDoubleClick}
      onClick={handleRootClick}
      onPointerDownCapture={handlePointerDownCapture}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={handleMouseLeave}
    >
      {/* Placeholder until the live document paints: the server-rendered
          card preview when we have one (share view), else the same loading
          skeleton the node wore on the way in. Frameless blocks skip the
          plate, and transparent blocks skip the preview too — the JPEG is
          opaque, and an opaque plate under a see-through block breaks the
          canvas showing through (worse than briefly showing nothing). A
          capture is the exception: a whole-canvas one never mounts the
          document, so the poster or titled plate is all the block will
          ever show. */}
      {!isVisible && (
        <HtmlCardPlaceholder
          previewImageUrl={
            transparent && !captureMount ? null : previewImageUrl
          }
          showPlate={!frameless || captureMount}
          capture={captureMount}
          pending={captureMount && admitted}
          title={displayTitle}
        />
      )}
      <div
        className={cn(
          // Instant swap from the placeholder: an opacity transition inside
          // the canvas viewport composites it in WebKit and blurs every
          // node for its duration.
          "absolute inset-0",
          isVisible ? "opacity-100" : "opacity-0",
          !transparent && !frameless && "bg-card",
        )}
        style={{ colorScheme: theme }}
      >
        {displayDoc && admitted && (
          <iframe
            key={displayDoc.mountKey}
            ref={iframeRef}
            srcDoc={displayDoc.srcDoc}
            className="absolute inset-0 h-full w-full border-0"
            style={{
              pointerEvents: iframeActive || shieldOpen ? "auto" : "none",
              colorScheme: theme,
              background: "transparent",
            }}
            sandbox="allow-scripts allow-forms"
            title={displayTitle}
            onLoad={markDisplayReady}
          />
        )}
      </div>
      {/* Host-DOM hit plate: guarantees canvas gestures (wheel pan, drag pan,
          marquee) work over the block whenever the content doesn't own the
          pointer. Double-clicks bubble to the card root. */}
      {stateBridge.unavailable && (
        <div className="pointer-events-none absolute right-2 top-2 rounded-md bg-n-95/80 px-2 py-1 text-xs text-n-00">
          Data unavailable
        </div>
      )}
      {!iframeActive && (
        <HtmlBlockShield
          hintLabel={hintLabel}
          cursor={hoverKind === "island" ? "text" : "default"}
          open={shieldOpen}
        />
      )}
    </div>
  );
}
